import type { ReactNode } from "react";
import type React from "react";
import type {
  FlatListProps,
  StyleProp,
  TextInputProps,
  ViewStyle,
  ImageProps,
  ViewProps,
} from "react-native";
import type { ImageStyle, Source } from "./fastImage";
import type { SelectProps } from "antd";

export interface IRTKModels {
  [key: string]: any;
}

export interface IModalConfirmProps {
  onConfirmClick?: (arg?: any) => void;
  onCancelClick?: () => void;
  title?: string;
  message?: string;
  content?: ReactNode;
  children?: ReactNode;
  showClose?: boolean;
  showCancelButton?: boolean;
  showConfirmButton?: boolean;
  cancelButtonText?: string;
  confirmButtonText?: string;
  confirmButtonStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
  messageStyle?: StyleProp<ViewStyle>;
  testID?: string;
  onHide?: () => void;
  loading?: boolean;
  maskClosable?: boolean;
  destroyOnClose?: boolean;
  isVisible: boolean;
  // web端适用 渲染弹窗底部内容
  footer?: React.ReactNode;
}

export interface IModalSlideProps {
  onConfirmClick?: () => void;
  avoidKeyboard?: boolean;
  children?: ReactNode;
  showClose?: boolean;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  onHide?: () => void;
  isVisible: boolean;
}

export interface IListViewProps<T> extends FlatListProps<T> {
  finishText?: string;
  onRefresh?: () => void;
  onLoadMore?: () => void;
  loadStatus?: string;
  viewCompliance?: boolean;
  module?: string;
  viewDisclaimer?: boolean;
}

export enum ILoadStatusList {
  LOADING = "loading",
  ERROR = "error",
  FINISH = "finish",
  NONE = "none",
}

export interface ICommonInputProps extends TextInputProps {
  //经过精度处理过的value,暂时只对 valueType="number" 的情况生效,valueType是text|email 该方法同等于onChangeText回调
  onValueChange?: ((text: string) => void) | undefined;
  valueType?: "text" | "number" | "email"; //输入的数据类型
  precision?: number; //valueType=number的时候,数据精度
}

// 从adg-app 迁移过来的选项类型,用于底部选择框和单选多选框
export interface IOptionItem {
  title: string;
  value: string;
  icon?: string;
  iconSource?: Source;
  iconStyle?: StyleProp<ImageStyle>;
  testID?: string;
  accessibilityLabel?: string;
  commonName?: string;
  countryCode?: string;
  /** 描述文字，用于在输入框上方显示（主要用于 radioWithInput 类型的选项） */
  description?: string;
  /** 输入框样式，用于自定义 radioWithInput 类型选项的输入框样式 */
  inputStyle?: TextInputProps["style"];
}

/**
 * 表单Layout公共props
 */
export interface IFormItemLayout<T> extends ViewProps {
  error?: string | undefined;
  label?: string | React.ReactNode | undefined;
  subLabel?: string | React.ReactNode | undefined;
  tip?: string | undefined;
  containerStyle?: StyleProp<ViewStyle>;
  labelElement?: React.ReactNode;
  hideErrorSpace?: boolean;
  sequenceNumber?: number; //表单序号，用于显示编号（如：01、02等
  // TODO:kyc: 该定义待定，后续可能会更改
  horizontalLayout?: boolean; // 表示 fields 是否采用横向布局。如果是FormItemLayout需要额外处理样式
  value?: T;
  placeholder?: string;
  onValueChange?: (value: T) => void;
  required?: boolean; // 表单题目是否必填
}

// ===================Select组件相关props===================
/**
 * CommonSelect Web端组件属性接口
 *
 * @description 继承Ant Design SelectProps，扩展Web端特定功能
 * @extends SelectProps<string,any>
 * @since 1.0.0
 */
export interface TCommonSelectWebProps extends SelectProps<string, any> {
  /** 自定义样式对象，用于覆盖默认样式 */
  style?: React.CSSProperties;
  /** 是否启用多选 */
  multiple?: boolean;
}

/**
 * CommonSelect Native端组件属性类型
 *
 * @description 定义React Native端选择器组件的属性，主要用于底部弹出选择器
 * @since 1.0.0
 */
export type TCommonSelectNativeProps = {
  value: string | string[];
  /** 占位符文本，当无选中项时显示 */
  placeholder?: string;
  /** 选择确认回调函数，用户选择选项时触发 */
  onSelect: (value: string) => void;
  /** 选项数据数组，每个选项包含value和title */
  options: IOptionItem[];
  /** 是否禁用组件，禁用时不可交互，默认 false */
  disabled?: boolean;
  /** 是否允许清除已选项，默认 true */
  allowClear?: boolean;
  /** 自定义筛选逻辑函数，用于搜索时过滤选项 */
  filterOption?: (input: string, option: IOptionItem) => boolean;
  /** 是否显示搜索框，默认根据选项数量自动决定（>=20项时显示） */
  showSearch?: boolean;
  /** 自定义样式对象，用于覆盖默认样式 */
  style?: StyleProp<ViewStyle>;
  /** 弹窗隐藏回调函数，当用户关闭选择器时触发 */
  onHide?: () => void;
  /** 是否启用多选 */
  multiple?: boolean;
};

/**
 * AsSelect 表单选择器组件属性接口
 * @description KYC表单专用选择器组件属性，继承表单项和视图基础属性，
 *              集成了完整的表单验证、布局和选择功能
 * @extends IFormItemLayout 表单项基础属性（label、error、tip等）
 */
export interface IAsSelectProps extends IFormItemLayout<string | string[]> {
  /** 选项数据数组，每个选项包含value、title等字段 */
  options: IOptionItem[];
  /**
   * 自定义筛选逻辑函数，用于实现复杂的搜索匹配
   * @param input 用户输入的搜索关键词
   * @param option 当前选项对象
   * @returns 是否匹配，true表示显示该选项
   */
  filterOption?: (input: string, option: IOptionItem) => boolean;
  /** 是否显示搜索框，默认根据选项数量自动决定（>=20项时显示） */
  showSearch?: boolean;
  /** 是否禁用组件，禁用时显示灰色状态且不可交互，默认 false */
  disabled?: boolean;
  /** 是否启用多选 */
  multiple?: boolean;
  className?: string;
}

// ===================DatePicker组件相关props===================
/**
 * 日期选择器组件 Props 接口
 */
export interface ICommonDatePickerProps {
  dateValue: string;
  /** 占位符文本 */
  placeholder?: string;
  /** 日期确认回调函数 */
  onChange: (date: string) => void;
  /** 是否禁用，默认 false */
  disabled?: boolean;
  /** 是否允许清除，默认 true */
  allowClear?: boolean;
}

export interface TDatePickerWebProps extends ICommonDatePickerProps {
  style?: React.CSSProperties;
  value: string;
}

export interface TDatePickerNativeProps extends ICommonDatePickerProps {
  /** 日期格式 */
  dateFormat: string;
  style?: StyleProp<ViewStyle>;
  /** 显示语种 */
  locale?: string;
  date?: string;
  current?: string;
  mode?: "datepicker" | "calendar" | "monthYear" | "time";
}

export interface IKycDateTimePickerProps extends IFormItemLayout<string> {
  /** 日期格式*/
  dateFormat: string;
  /** 是否禁用 */
  disabled?: boolean;
  locale?: string;
  /** 是否启用多选 */
  multiple?: boolean;
}

// ===================单选/多选组件相关props===================
export type TCommonRadioProps = {
  value?: any;
  checked: boolean;
  key?: string;
  label?: string;
  children?: React.ReactNode;
  onChange?: (value: string) => void;
  style?: any; // 跨平台组件
};

export type TCommonCheckboxProps = TCommonRadioProps;

/**
 * AsCheckboxGroup 多选框组件属性接口
 *
 * @description KYC表单专用多选框组件属性，支持多选功能，value为数组类型
 * @extends ViewProps 视图基础属性（style等）
 * @usage 用于KYC表单中的多选场景，如风险评估问卷、投资偏好选择等
 */
export interface TKycCheckboxGroupProps extends IFormItemLayout<string[]> {
  /** 选项数据数组，每个选项包含value、title等字段 */
  options: IOptionItem[];
  /** 自定义下拉选择的选项数据，用于支持带下拉选择的多选框 */
  customSelectOptions?: IOptionItem[];
}

export interface IKycRadioGroupProps extends IFormItemLayout<string> {
  /** 选项列表 */
  options: IOptionItem[];
  customSelectOptions?: IOptionItem[];
}

/**
 * 分类项接口
 * @description 用于 KycCategoryRadioGroup 组件，定义每个分类的结构
 */
export interface ICategoryItem {
  /** 分类的唯一标识和显示标题 */
  key: string;
  /** 该分类下的选项列表 */
  options: IOptionItem[];
}

/**
 * KycCategoryRadioGroup 组件属性接口
 *
 * @description KYC表单专用分类单选组组件属性，支持多个分类，每个分类下有独立的选项列表
 * @extends IFormItemLayout 表单项基础属性（label、error、tip等）
 * @usage 用于KYC表单中需要按分类进行单选选择的场景，如投资经验分类选择等
 */
export interface IKycCategoryRadioGroupProps extends IFormItemLayout<Record<
  string,
  string
> | null> {
  /** 分类列表，每个分类包含 key 和独立的 options */
  categories: ICategoryItem[];
  /** Web 端选项之间的横向间距（px），默认 100 */
  optionGap?: number;
}

export type TGenericFormType = "information" | "questionnaire";

export interface ICommonImageProps extends ImageProps {
  width?: number;
  height?: number;
  alt?: string;
}

export interface IModalBottomSelectorProps extends IModalSlideProps {
  onSelect: (value: string | string[], item?: IOptionItem) => void;
  isVisible: boolean;
  showCoins?: boolean;
  options: IOptionItem[];
  searchAble?: boolean; //是否开启选项搜索功能
  iconType?: "emoji" | "image";
  selectItemLeft?: boolean; // 下拉选项是否居左，默认居中
  filterOption?: (input: string, option: IOptionItem) => boolean; // 自定义筛选逻辑
  value: string | string[];
  multiple?: boolean;
}
